import { Injectable } from '@angular/core';
import { AbstractStatsService } from './abstract.stats.service';
import { Options } from 'highcharts';
import Highcharts from 'highcharts';
import { Subject, Observable } from 'rxjs';
import { BaseAPIService } from '@app/Services/api.base.service';
import { label } from '@app/constants/labels';
import { IAlarmsByDeviceStatusFilter } from '../../Models/IStatistics';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class StatsService implements AbstractStatsService {

  labels: any = label;
  private readonly colChartClick = new Subject<any>();
  private readonly pieChartClick = new Subject<any>();
  private readonly stockChartClick = new Subject<any>();
  colors = ['#F92E02', '#F5F902', '#B9B9B5'];
  colChartClicked$ = this.colChartClick.asObservable();
  pieChartClicked$ = this.pieChartClick.asObservable();
  stockChartClicked$ = this.stockChartClick.asObservable();

  constructor(private readonly baseApiService: BaseAPIService, private readonly translate: TranslateService) {

  }

  pieChartConfig: Highcharts.Options = {
    chart: {
      type: 'pie'
    }, accessibility: {
      enabled: false
    },
    colors: this.colors,
    exporting: {
      fallbackToExportServer: false
    },
    title: {
      text: '',
    },
    navigation: {
      buttonOptions: {
        enabled: false
      }
    },
    plotOptions: {
      series: {
        cursor: 'pointer',
        events: {
          click: (data) => {
            this.pieChartClicked(data.point.options);
          }
        }
      },
      pie: {
        startAngle: 90
      }
    },
    credits: {
      enabled: false
    },
    series: [{
      type: 'pie',
      name: 'Devices'
    }]
  };

  stockChartConfig: Highcharts.Options = {
    rangeSelector: {
      selected: 2,
      allButtonsEnabled: true,
      inputEnabled: true,
      enabled: true,
    }, accessibility: {
      enabled: false
    },
    xAxis: {
      type: 'datetime',
    },
    colors: this.colors,
    title: {
      text: '',
    },
    legend: {
      enabled: true
    },
    tooltip: {
      split: false,
      shared: true,
      useHTML: true,

      formatter: function (this: Highcharts.TooltipFormatterContextObject,
        tooltip: Highcharts.Tooltip) {
        const dateTimeHtml = `${new Date(this.x).toUTCString()}<br/>`;
        let tooltipHtml = dateTimeHtml;
        for (let i = 0; i < this.points.length; i++) {
          const point = this.points[i];
          tooltipHtml += `${point.series.name} Alarm Count: ${point.y} <br />`;
        }
        return tooltipHtml;
      }
    },
    plotOptions: {
      series: {
        dataGrouping: {
          enabled: false
        },
        enableMouseTracking: true,
        showInNavigator: true,
        cursor: 'pointer',
        events: {
          click: (data) => {
            this.stockChartClicked(data.point);
          }
        }
      }
    },
    credits: {
      enabled: false
    },
    exporting: {
      fallbackToExportServer: false
    },
    navigation: {
      buttonOptions: {
        enabled: false
      }
    },
  };


  colChartClicked(data) {
    this.colChartClick.next(data);
  }

  pieChartClicked(group) {
    this.pieChartClick.next(group);
  }

  stockChartClicked(group) {
    this.stockChartClick.next(group);
  }


  GroupDeviceInfoByDeviceStatus(groupId: number, status: number): Observable<any> {
    return this.baseApiService.GET(`/Statistics/GroupDeviceInfoByDeviceStatus/${groupId}?deviceStatus=${status}`);
  }

  get deviceStatusValueLabels(): any[] {
    return [{
      "key": "all",
      "value": this.translate.instant(this.labels.applyToLab2)
    },
    {
      "key": "active",
      "value": this.translate.instant(this.labels.tableDataDeviceActive)
    },
    {
      "key": "inactive",
      "value": this.translate.instant(this.labels.tableDataDeviceInActive)
    }
    ]
  }

  GetDataForAlarmStockChart(deviceFilter: IAlarmsByDeviceStatusFilter): Observable<any> {

    return this.baseApiService.POST(`/Statistics/GetDataForAlarmStockChart`, deviceFilter);
  }

  get alarmsByDeviceOverTimeStockConfig(): Options {
    return this.stockChartConfig;
  }

  GetDeviceListData(): Observable<any> {
    return this.baseApiService.GET('/DeviceDashboard/DeviceDetailForDashboard');
  }

  GetDataForStatisticsBarChart(deviceFilter: IAlarmsByDeviceStatusFilter, startDate: any, endDate: any): Observable<any> {
    return this.baseApiService.POST(`/Statistics/GetDataForStatisticsBarChart?startDate=${startDate}&endDate=${endDate}`, deviceFilter);
  }

  GetDataForStatisticsPieChart(deviceFilter: IAlarmsByDeviceStatusFilter, startDate: any, endDate: any): Observable<any> {
    return this.baseApiService.POST(`/Statistics/GetDataForStatisticsPieChart?startDate=${startDate}&endDate=${endDate}`, deviceFilter);
  }
  GetGroupsByGroupIdsAndDeviceStatus(deviceFilter: IAlarmsByDeviceStatusFilter): Observable<any> {
    return this.baseApiService.POST('/Statistics/GetGroupsByGroupIdsAndDeviceStatus', deviceFilter);
  }
  get getdeviceByGroupPieChartConfig(): Options {
    return this.pieChartConfig;
  }

  GetAlarmsCountByDistriConfig(xCategories: any, data: any): Options {
    const config: Highcharts.Options = {
      chart: {
        polar: true,
        type: 'line'
      }, accessibility: {
        enabled: false
      },
      title: {
        text: '',
        x: -80
      },

      xAxis: {
        categories: xCategories,
        tickmarkPlacement: 'on',
        lineWidth: 0
      },

      yAxis: {
        gridLineInterpolation: 'polygon',
        lineWidth: 0,
        min: 0
      },

      series: [{
        name: this.translate.instant(this.labels.statAlarmCountTitle),
        data: data,
        type: undefined,
        pointPlacement: 'on'
      }],
      credits: {
        enabled: false
      },
      exporting: {
        fallbackToExportServer: false
      },
      navigation: {
        buttonOptions: {
          enabled: false
        }
      }
    }
    return config;
  }

  GetdeviceStatusCountColumnChartConfig(result, xAxisCategory) {
    return {
      chart: {
        type: 'column'
      },
      colors: this.colors,
      title: {
        text: '',
        align: 'left'
      },

      xAxis: {
        categories: xAxisCategory
      },
      accessibility: {
        enabled: false
      },
      yAxis: {
        allowDecimals: false,
        min: 0,
        title: {
          text: this.translate.instant(this.labels.statAlarmCountTitle),
        },
        stackLabels: {
          enabled: true
        }
      },
      plotOptions: {
        series: {
          cursor: 'pointer',
          events: {
            click: (data) => {
              this.colChartClicked(data);
            }
          }
        },
        column: {
          stacking: 'normal'
        }
      },
      series: result,
      credits: {
        enabled: false
      },
      exporting: {
        fallbackToExportServer: false
      },
      navigation: {
        buttonOptions: {
          enabled: false
        }
      }
    }
  }

  get deviceStatusCountDateRangeLabelsColumn(): any[] {
    return this.DatewithMonthRange();
  }

  DatewithMonthRange() {
    return [
      {
        "key": 1,
        "value": this.translate.instant(this.labels.filterDay)
      },
      {
        "key": 7,
        "value": this.translate.instant(this.labels.filterWeek)
      },
      {
        "key": 30,
        "value": this.translate.instant(this.labels.filterLastMonth)
      },
      {
        "key": 90,
        "value": this.translate.instant(this.labels.filterLast3Month)
      },
      {
        "key": 365,
        "value": this.translate.instant(this.labels.filterLastYear)
      }
    ]
  }
  monthRange() {
    return [{
      "key": 30,
      "value": this.translate.instant(this.labels.filterLastMonth)
    },
    {
      "key": 90,
      "value": this.translate.instant(this.labels.filterLast3Month)
    },
    {
      "key": 180,
      "value": this.translate.instant(this.labels.filterLast6Month)
    },
    {
      "key": 365,
      "value": this.translate.instant(this.labels.filterLastYear)
    }
    ]
  }
  get distributionTimeRange(): any[] {
    return [
      {
        "key": 0,
        "value": this.translate.instant(this.labels.filterHour)
      },
      {
        "key": 1,
        "value": this.translate.instant(this.labels.filterDay)
      },
      {
        "key": 2,
        "value": this.translate.instant(this.labels.filterMonth)
      }]
  }


  get deviceCountDateRangeLabels(): any[] {
    return this.monthRange();
  }

  GetDeviceCountByUser(): Observable<any> {
    return this.baseApiService.GET('/Statistics/DeviceCountByUser');
  }

  GetDeviceCountByStatus(status: number): Observable<any> {
    return this.baseApiService.GET(`/Statistics/DeviceCountByStatus?deviceStatus=${status}`);
  }

  GetGroupByUserAndDeviceStatus(status: number): Observable<any> {
    return this.baseApiService.GET(`/Statistics/GroupByUserAndDeviceStatus?deviceStatus=${status}`);
  }
  GetControllerTypeByGroups(groupIds: Array<number>, status: number): Observable<any> {
    const _param = {
      DeviceStatus: status,
      GroupIds: groupIds
    }
    return this.baseApiService.POST(`/Statistics/ControllerTypeByGroups`, _param);
  }
  get deviceStatusData(): any[] {
    return [
      {
        "key": 1,
        "value": this.translate.instant(this.labels.online)
      },
      {
        "key": 0,
        "value": this.translate.instant(this.labels.offline)
      }, {
        "key": 3,
        "value": this.translate.instant(this.labels.tableDataDeviceInActive)
      },
      {
        "key": 2,
        "value": this.translate.instant(this.labels.all)
      }

    ]
  }

  GetAlarmDataForBarchartClick(deviceId: number, status: number, isActive: boolean, startDate: any, endDate: any): Observable<any> {
    return this.baseApiService.GET(`/Statistics/GetAlarmDataForBarchartClick/${deviceId}?severity=${status}&isActive=${isActive}&startDate=${startDate}&endDate=${endDate}`);
  }
  GetAlarmDataForPiechartClick(deviceFilter: IAlarmsByDeviceStatusFilter, status: number, isActive: boolean, startDate: any, endDate: any): Observable<any> {
    return this.baseApiService.POST(`/Statistics/GetAlarmDataForPiechartClick?severity=${status}&isActive=${isActive}&startDate=${startDate}&endDate=${endDate}`, deviceFilter);
  }
  GetDataForAlarmStockChartClick(deviceFilter: IAlarmsByDeviceStatusFilter, status: number, isActive: boolean, selectedDate: any): Observable<any> {
    return this.baseApiService.POST(`/Statistics/GetDataForAlarmStockChartClick?severity=${status}&isActive=${isActive}&timeStamp=${selectedDate}`, deviceFilter);
  }
  GetDataForAlarmRadarChart(deviceFilter: IAlarmsByDeviceStatusFilter, timeInterval: number): Observable<any> {
    return this.baseApiService.POST(`/Statistics/GetDataForAlarmRadarChart?timeInterval=${timeInterval}`, deviceFilter);

  }
}

